import React, { useState } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { createPatient, searchBreeds } from "./api";
import { Patient } from "../../types";

interface Props {
  show: boolean;
  onHide: () => void;
  onCreateSuccess: (patientData: Patient) => void;
}

const CreatePatientModal: React.FC<Props> = ({ show, onHide, onCreateSuccess }) => {
  const [form, setForm] = useState({
    animalName: "",
    species: "",
    sex: "",
    breed: "",
    address: "",
    phone: "",
    ownerFirstName: "",
    ownerLastName: "",
  });
  const [breedOptions, setBreedOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (field: string, value: string) => {
    setForm({ ...form, [field]: value });
    if (field === "breed" && value.length >= 2) {
      searchBreeds(value).then(setBreedOptions);
    }
  };

  const validate = () => {
    const missing = Object.entries(form)
      .filter(([, v]) => !v.trim())
      .map(([k]) => k);
    setErrors(missing);
    return missing.length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const patientData = await createPatient(form);
      onHide();
      onCreateSuccess(patientData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Patient</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col><Form.Group>
              <Form.Label>Patient Name</Form.Label>
              <Form.Control
                value={form.animalName}
                onChange={(e) => handleChange("animalName", e.target.value)}
                isInvalid={errors.includes("animalName")}
              />
            </Form.Group></Col>
            <Col><Form.Group>
              <Form.Label>Species</Form.Label>
              <Form.Select
                value={form.species}
                onChange={(e) => handleChange("species", e.target.value)}
                isInvalid={errors.includes("species")}
              >
                <option value="">Select...</option>
                <option>Canine</option>
                <option>Feline</option>
              </Form.Select>
            </Form.Group></Col>
          </Row>
          <Row className="mt-3">
            <Col><Form.Group>
              <Form.Label>Sex</Form.Label>
              <Form.Select
                value={form.sex}
                onChange={(e) => handleChange("sex", e.target.value)}
                isInvalid={errors.includes("sex")}
              >
                <option value="">Select...</option>
                <option>female - intact</option>
                <option>female - spayed</option>
                <option>male - neutered</option>
                <option>male - intact</option>
              </Form.Select>
            </Form.Group></Col>
            <Col><Form.Group>
              <Form.Label>Breed</Form.Label>
              <Form.Control
                list="breed-options"
                value={form.breed}
                onChange={(e) => handleChange("breed", e.target.value)}
                isInvalid={errors.includes("breed")}
              />
              <datalist id="breed-options">
                {breedOptions.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </Form.Group></Col>
          </Row>
          <Form.Group className="mt-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              isInvalid={errors.includes("address")}
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              isInvalid={errors.includes("phone")}
            />
          </Form.Group>
          <Row className="mt-3">
            <Col><Form.Group>
              <Form.Label>Owner First Name</Form.Label>
              <Form.Control
                value={form.ownerFirstName}
                onChange={(e) => handleChange("ownerFirstName", e.target.value)}
                isInvalid={errors.includes("ownerFirstName")}
              />
            </Form.Group></Col>
            <Col><Form.Group>
              <Form.Label>Owner Last Name</Form.Label>
              <Form.Control
                value={form.ownerLastName}
                onChange={(e) => handleChange("ownerLastName", e.target.value)}
                isInvalid={errors.includes("ownerLastName")}
              />
            </Form.Group></Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : "Create Patient"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreatePatientModal;